/**
 * branding.js — Branding AI prompt tasks. Schema and quality bar: see _exemplar.js.
 */
export const BRANDING_TASKS = [
  // ── Brand name generator ──────────────────────────────────────────────────
  {
    id: "branding-name-generator",
    category: "branding",
    title: "Generate Brand Names",
    description: "25 brand-name candidates across five naming styles, each with rationale, pronunciation, a preliminary trademark-risk read and domain notes — plus a shortlist and a clearance plan.",
    outputType: "text",
    recommendedTools: ["chatgpt", "claude", "gemini"],
    estimatedTime: "2 min to fill · 1 min in ChatGPT",
    fields: [
      { id: "brandName", label: "Working name (if any)", type: "text", placeholder: "Leave blank if you are starting from zero", brandKey: "brandName", help: "Names will be generated as alternatives to this." },
      { id: "category", label: "Product category", type: "text", placeholder: "Energy drink", required: true, brandKey: "category" },
      { id: "productDescription", label: "Product description", type: "textarea", placeholder: "12 oz sleek can, zero sugar, 200 mg natural caffeine", required: true, brandKey: "productDescription", span: "full" },
      { id: "targetConsumer", label: "Target consumer", type: "textarea", placeholder: "Men and women 18–35 interested in fitness", required: true, brandKey: "targetConsumer", span: "full" },
      { id: "positioning", label: "Brand positioning", type: "textarea", placeholder: "Natural premium performance energy", required: true, brandKey: "positioning", span: "full" },
      { id: "personality", label: "Brand personality", type: "tags", placeholder: "Confident, Clean, Energetic", brandKey: "personality" },
      { id: "claims", label: "Key claims", type: "tags", placeholder: "Zero sugar, Natural caffeine", brandKey: "claims" },
      { id: "competitors", label: "Competitors", type: "tags", placeholder: "Celsius, Alani Nu, Ghost", brandKey: "competitors" },
      { id: "avoid", label: "What should AI avoid?", type: "textarea", placeholder: "Anything that sounds like a pharmaceutical; bro-culture names", brandKey: "avoid" },
      { id: "namingStyles", label: "Naming style preferences", type: "tags", placeholder: "Evocative, Invented, Short real words", help: "Leave blank to get an even spread of all five styles." },
      { id: "wordsInclude", label: "Words, roots or themes to include", type: "tags", placeholder: "Rise, Fuel, Sun, Latin roots for energy" },
      { id: "wordsAvoid", label: "Words or themes to avoid", type: "tags", placeholder: "Beast, Monster, Rage, Power" },
      { id: "nameLength", label: "Name length", type: "select", options: ["Short (1–2 syllables)", "Medium (2–3 syllables)", "No preference"], default: "No preference" },
      { id: "domainPreference", label: "Domain requirement", type: "select", options: [".com strongly preferred", ".com or .co acceptable", "Any extension — we will add a modifier"], default: ".com or .co acceptable" },
    ],
    template: `Act as a senior brand naming strategist who has named CPG brands later acquired by major food and beverage companies, with working knowledge of USPTO trademark classes and the realities of domain availability.

Generate 25 brand-name candidates for {{category|article}}.{{#if brandName}} Current working name: {{brandName}} — treat it as a benchmark to beat, not a constraint.{{/if}}

PRODUCT
{{productDescription}}

BRAND POSITIONING
{{positioning}}

TARGET CONSUMER
{{targetConsumer}}

{{#if personality}}
PERSONALITY THE NAME MUST CARRY
{{personality|join}}.
{{/if}}

{{#if claims}}
KEY ATTRIBUTES
{{claims|list}}
{{/if}}

{{#if competitors}}
COMPETITIVE SET
{{competitors|join}}. Names must not sound, look or feel like any of these, and must avoid their naming conventions.
{{/if}}

NAMING PARAMETERS
Length: {{nameLength}}. Domain: {{domainPreference}}.
{{#if namingStyles}}
Lean toward these styles: {{namingStyles|join}} — but still cover all five buckets below.
{{/if}}
{{#if wordsInclude}}
Words, roots or themes to explore: {{wordsInclude|join}}.
{{/if}}
{{#if wordsAvoid}}
Never use: {{wordsAvoid|join}}.
{{/if}}

{{#if avoid}}
AVOID
{{avoid|list}}
- Names already used by a known food, beverage or supplement brand
- Hard-to-spell or hard-to-pronounce names that fail the radio test
{{/if}}
{{#unless avoid}}
AVOID
- Names already used by a known food, beverage or supplement brand
- Hard-to-spell or hard-to-pronounce names that fail the radio test
{{/unless}}

PROVIDE
1. 25 names, exactly 5 in each of these five styles: (a) Descriptive / real word, (b) Evocative / metaphor, (c) Invented / coined, (d) Compound / portmanteau, (e) Founder, place or story-based. Present as ONE table: name | style | pronunciation | one-line rationale | natural tagline pairing | preliminary trademark risk (Low / Medium / High with a reason) | domain outlook (likely available exact-match, likely needs a modifier, likely taken)
2. The radio test: mark any name that could be misheard or misspelled when said out loud
3. Shortlist: the top 5 across all styles, ranked, with a paragraph each on why it wins for this consumer and how it would look on {{category|article}} pack at 1 inch wide
4. For each shortlisted name: two alternate spellings or modifiers that improve domain and trademark odds
5. Clearance plan: the exact steps to screen the shortlist — USPTO search (knock-out search in the relevant international classes for this product), state business registries, exact-match and modifier domains, social handles, a plain web search, and a cross-language check — and a clear statement that a trademark attorney must run a full clearance before any money is spent on design
6. Three "category-flip" wildcard names that break the conventions of the {{category|lower}} aisle entirely

Trademark risk ratings are educated guesses from pattern knowledge, not legal advice — say so once at the top of the table. Do not claim a domain or trademark is definitely available.`,
    tips: [
      "Paste your top 5 into Perplexity and ask: “Search USPTO and the web for existing food or beverage brands using these names.”",
      "Then run Create Logo Concept Image with your favorite name in quotes to see it as a wordmark.",
    ],
  },

  // ── Logo direction ────────────────────────────────────────────────────────
  {
    id: "branding-logo-direction",
    category: "branding",
    title: "Create Logo Direction",
    description: "Three logo territories — wordmark, monogram and emblem — each with typography, color, lockups, on-pack behavior and an image prompt, plus a recommendation on which to develop.",
    outputType: "text",
    recommendedTools: ["chatgpt", "claude", "gemini"],
    estimatedTime: "2 min to fill · 1 min in ChatGPT",
    fields: [
      { id: "brandName", label: "Brand name", type: "text", placeholder: "XYZ", required: true, brandKey: "brandName" },
      { id: "category", label: "Product category", type: "text", placeholder: "Energy drink", required: true, brandKey: "category" },
      { id: "positioning", label: "Brand positioning", type: "textarea", placeholder: "Natural premium performance energy", required: true, brandKey: "positioning", span: "full" },
      { id: "targetConsumer", label: "Target consumer", type: "textarea", placeholder: "Men and women 18–35 interested in fitness", brandKey: "targetConsumer", span: "full" },
      { id: "personality", label: "Brand personality", type: "tags", placeholder: "Confident, Clean, Energetic", brandKey: "personality" },
      { id: "style", label: "Desired style", type: "tags", placeholder: "Premium, Modern, Minimal", brandKey: "visualStyle" },
      { id: "brandColors", label: "Existing brand colors (optional)", type: "tags", placeholder: "Leave blank to let AI propose", brandKey: "brandColors" },
      { id: "fonts", label: "Existing fonts (optional)", type: "tags", placeholder: "Inter, Playfair Display", brandKey: "fonts" },
      { id: "competitors", label: "Competitors", type: "tags", placeholder: "Celsius, Alani Nu, Ghost", brandKey: "competitors" },
      { id: "avoid", label: "What should AI avoid?", type: "textarea", placeholder: "Lightning bolts; aggressive italics", brandKey: "avoid" },
      { id: "primaryApplication", label: "Where the logo lives first", type: "select", options: ["Can or bottle", "Pouch or bag", "Folding carton or box", "Jar or tub", "Digital-first (website, social)"], default: "Can or bottle" },
      { id: "logoFeel", label: "Logo feel", type: "select", options: ["Let AI recommend", "Modern minimal", "Heritage / craft", "Playful / friendly", "Premium / luxury", "Bold / athletic", "Natural / organic"], default: "Let AI recommend" },
      { id: "symbolIdeas", label: "Symbol or icon ideas (optional)", type: "textarea", placeholder: "A sunrise, a leaf, a hidden arrow in the letterforms" },
    ],
    template: `Act as a senior brand identity designer who has built logo systems for CPG brands on shelf at Target and Whole Foods and who thinks about how a mark performs at 1 inch on a pack before anything else.

Develop three distinct logo territories for "{{brandName}}", {{category|article}}. Primary application: {{primaryApplication|lower}}. Desired feel: {{logoFeel}}.

BRAND POSITIONING
{{positioning}}

{{#if targetConsumer}}
TARGET CONSUMER
{{targetConsumer}}
{{/if}}

{{#if personality}}
PERSONALITY
{{personality|join}}.
{{/if}}

VISUAL DIRECTION
{{style|list}}
{{#unless style}}
- Premium
- Modern
- Distinctive
{{/unless}}
{{#if brandColors}}
Existing colors to work with: {{brandColors|join}}.
{{/if}}
{{#if fonts}}
Existing fonts to consider: {{fonts|join}}.
{{/if}}
{{#if symbolIdeas}}
Symbol ideas to explore: {{symbolIdeas}}
{{/if}}

{{#if competitors}}
COMPETITIVE SET
{{competitors|join}}. Describe the logo codes they share and make sure each territory escapes them.
{{/if}}

{{#if avoid}}
AVOID
{{avoid|list}}
- Generic category symbols (leaves for natural, bolts for energy) unless reinvented
- Anything illegible below 0.5 inch
{{/if}}
{{#unless avoid}}
AVOID
- Generic category symbols (leaves for natural, bolts for energy) unless reinvented
- Anything illegible below 0.5 inch
{{/unless}}

THE THREE TERRITORIES
Territory A — Wordmark: the name itself is the logo.
Territory B — Monogram / lettermark: an initial or abbreviation as a compact mark, paired with the name.
Territory C — Emblem / badge: name and symbol contained in a shape.

FOR EACH TERRITORY PROVIDE
1. Territory name and one-sentence big idea
2. Rationale: why it fits the positioning and the target consumer
3. Typography: a specific primary typeface (a free Google Fonts option AND a premium alternative), weight, case, letter-spacing, and any custom letterform modifications
4. Symbol or device (if any): what it is, how it relates to the name, how it works alone as an app icon or tab
5. Color application: primary, secondary and one-color versions with hex codes
6. Lockups required: primary, stacked, horizontal, icon-only — and which goes on the {{primaryApplication|lower}}
7. Minimum size and clear space rule
8. On-pack behavior: how it reads at 1 inch wide, on a dark background, embossed or foiled
9. Differentiation score vs. the competitive set (1–10) with a sentence of justification
10. A ready-to-paste image-generation prompt for Ideogram or ChatGPT image generation that renders this logo on a plain white background, with the brand name in quotes exactly as "{{brandName}}"

Finish with: a comparison table (territory | ownability | shelf legibility | flexibility | effort to execute), a recommendation on which to develop first, and a 5-line brief I can hand a designer to execute it.`,
    tips: [
      "Paste each #10 prompt into Ideogram (best at spelling) — generate 4 variations per territory and pick by squinting at them from across the room.",
      "Then run Turn AI Logo Into Usable Vector Artwork to make the winner print-ready.",
    ],
  },

  // ── Brand identity system ─────────────────────────────────────────────────
  {
    id: "branding-identity-system",
    category: "branding",
    title: "Create Brand Identity System",
    description: "A complete identity system — personality, archetype, typography, color palette with hex codes, photography style, graphic language and a do/don't table — ready to become your brand guidelines.",
    outputType: "text",
    recommendedTools: ["claude", "chatgpt", "gemini"],
    estimatedTime: "3 min to fill · 2 min in Claude",
    fields: [
      { id: "brandName", label: "Brand name", type: "text", placeholder: "XYZ", required: true, brandKey: "brandName" },
      { id: "category", label: "Product category", type: "text", placeholder: "Energy drink", required: true, brandKey: "category" },
      { id: "productDescription", label: "Product description", type: "textarea", placeholder: "12 oz sleek can, zero sugar, 200 mg natural caffeine", brandKey: "productDescription", span: "full" },
      { id: "positioning", label: "Brand positioning", type: "textarea", placeholder: "Natural premium performance energy", required: true, brandKey: "positioning", span: "full" },
      { id: "targetConsumer", label: "Target consumer", type: "textarea", placeholder: "Men and women 18–35 interested in fitness", required: true, brandKey: "targetConsumer", span: "full" },
      { id: "personality", label: "Brand personality", type: "tags", placeholder: "Confident, Clean, Energetic", brandKey: "personality" },
      { id: "brandVoice", label: "Brand voice", type: "textarea", placeholder: "Direct and encouraging. Short sentences.", brandKey: "brandVoice", span: "full" },
      { id: "style", label: "Desired style", type: "tags", placeholder: "Premium, Modern, Minimal", brandKey: "visualStyle" },
      { id: "brandColors", label: "Existing brand colors (optional)", type: "tags", placeholder: "Leave blank to let AI propose", brandKey: "brandColors" },
      { id: "fonts", label: "Existing fonts (optional)", type: "tags", placeholder: "Inter", brandKey: "fonts" },
      { id: "competitors", label: "Competitors", type: "tags", placeholder: "Celsius, Alani Nu, Ghost", brandKey: "competitors" },
      { id: "founderStory", label: "Founder story (optional)", type: "textarea", placeholder: "Started by two trainers who...", brandKey: "founderStory", span: "full" },
      { id: "channels", label: "Sales channels", type: "tags", placeholder: "DTC, Amazon, Natural grocery", brandKey: "channels" },
      { id: "pricePoint", label: "Retail price point", type: "text", placeholder: "$2.99 per can", brandKey: "pricePoint" },
      { id: "avoid", label: "What should AI avoid?", type: "textarea", placeholder: "Neon colors; gaming imagery", brandKey: "avoid" },
      { id: "archetype", label: "Brand archetype", type: "select", options: ["Let AI recommend", "Hero", "Explorer", "Everyman", "Caregiver", "Creator", "Rebel", "Sage", "Jester", "Lover", "Magician", "Innocent", "Ruler"], default: "Let AI recommend" },
      { id: "primaryTouchpoint", label: "Most important touchpoint", type: "select", options: ["Packaging on shelf", "Website / DTC store", "Social media", "Amazon listing", "Retail sell sheets and buyer decks"], default: "Packaging on shelf" },
      { id: "guidelinesUse", label: "Who will use these guidelines?", type: "select", options: ["A freelance designer", "A design agency", "Me and my AI tools", "A co-packer or printer's in-house designer"], default: "A freelance designer" },
    ],
    template: `Act as a senior brand strategist and identity designer who has built brand systems for CPG brands from first shelf placement through national distribution, and who writes guidelines a freelancer, a co-packer's designer and a social manager can all follow.

Create a complete brand identity system for {{brandName}}, {{category|article}}.{{#if productDescription}} Product: {{productDescription}}.{{/if}} Archetype: {{archetype}}. The system must perform first on: {{primaryTouchpoint|lower}}. Primary reader of the guidelines: {{guidelinesUse|lower}} — pitch the level of detail accordingly.

BRAND POSITIONING
{{positioning}}

TARGET CONSUMER
{{targetConsumer}}

{{#if personality}}
PERSONALITY (starting point)
{{personality|join}}.
{{/if}}
{{#if brandVoice}}
VOICE (starting point)
{{brandVoice}}
{{/if}}

{{#if founderStory}}
FOUNDER STORY
{{founderStory}}
{{/if}}

VISUAL DIRECTION
{{style|list}}
{{#unless style}}
- Premium
- Modern
- Distinctive
{{/unless}}
{{#if brandColors}}
Existing colors to build around: {{brandColors|join}}.
{{/if}}
{{#if fonts}}
Existing fonts: {{fonts|join}}.
{{/if}}

{{#if competitors}}
COMPETITIVE SET
{{competitors|join}}. Map the visual and verbal codes they share so this system can deliberately own different territory.
{{/if}}

{{#if channels}}
WHERE THE BRAND SHOWS UP
{{channels|join}}.{{#if pricePoint}} Retail price: {{pricePoint}} — the system must look worth that price.{{/if}}
{{/if}}

{{#if avoid}}
AVOID
{{avoid|list}}
- Category clichés
- Trend-driven choices that will date within 18 months
{{/if}}
{{#unless avoid}}
AVOID
- Category clichés
- Trend-driven choices that will date within 18 months
{{/unless}}

PROVIDE
1. Brand essence: one line, then a 3-sentence brand idea
2. Personality: 5 traits, each with "how this shows up" (behavior) and "what it is not" (the failure mode)
3. Archetype: the recommended archetype{{#if archetype!=Let AI recommend}} (validate or challenge {{archetype}}){{/if}}, why it fits this consumer, and how it should shape visuals and voice
4. Typography system: primary and secondary typefaces (a free Google Fonts option AND a premium alternative for each), roles (headline, body, pack claims, numerals), weights, and a 5-step type scale
5. Color palette: a table with role (primary, secondary, accent, neutral light, neutral dark, 2 flavor or campaign colors) | name | hex | RGB | CMYK | nearest Pantone (mark as approximate, to be confirmed with the printer) | usage ratio | accessibility note (contrast on white and on the primary)
6. Photography style: subject, lighting, color grading, composition, casting and props — with 3 example shot descriptions
7. Graphic language: shapes, patterns, textures, line weights, icon style, how the logo and type interact with imagery
8. Layout principles: grid, whitespace, alignment, hierarchy for a pack front, a social post and a website hero — with the most detail on {{primaryTouchpoint|lower}}
9. Do / Don't table: 8 rows, each with a specific do, the matching don't, and why
10. Application notes: how the system flexes across pack, Amazon listing, Instagram, website, retail sell sheet and trade-show booth
11. One-page summary: the whole system in 150 words that {{guidelinesUse|lower}} can read in one minute

Use tables where listed. Be specific — every recommendation should be executable without a follow-up question.`,
    tips: [
      "Paste the result into Claude and ask it to format as a brand guidelines document with headings; then drop it into Gamma or Canva to design the PDF.",
      "Paste section 6 into Midjourney as a mood-board prompt to see the photography style before you brief a photographer.",
    ],
  },

  // ── Logo concept image (image) ────────────────────────────────────────────
  {
    id: "branding-logo-image-prompt",
    category: "branding",
    title: "Create Logo Concept Image",
    description: "An image-ready prompt that renders a clean logo concept for your brand name — pick wordmark, monogram, emblem or lockup and the style you want.",
    outputType: "image",
    recommendedTools: ["ideogram", "chatgpt-images", "recraft"],
    estimatedTime: "1 min to fill · 1 min to generate",
    fields: [
      { id: "brandName", label: "Brand name", type: "text", placeholder: "XYZ", required: true, brandKey: "brandName" },
      { id: "category", label: "Product category", type: "text", placeholder: "Energy drink", required: true, brandKey: "category" },
      { id: "positioning", label: "Brand positioning", type: "textarea", placeholder: "Natural premium performance energy", brandKey: "positioning", span: "full" },
      { id: "personality", label: "Brand personality", type: "tags", placeholder: "Confident, Clean, Energetic", brandKey: "personality" },
      { id: "style", label: "Desired style", type: "tags", placeholder: "Premium, Modern, Minimal", brandKey: "visualStyle" },
      { id: "brandColors", label: "Existing brand colors (optional)", type: "tags", placeholder: "#111111, #FF5A1F", brandKey: "brandColors" },
      { id: "avoid", label: "What should AI avoid?", type: "textarea", placeholder: "Lightning bolts; gradients", brandKey: "avoid" },
      { id: "logoType", label: "Logo type", type: "select", options: ["Wordmark", "Monogram / lettermark", "Emblem / badge", "Wordmark + icon lockup"], default: "Wordmark" },
      { id: "styleDirection", label: "Style direction", type: "select", options: ["Modern minimal sans-serif", "Heritage / craft serif", "Bold athletic", "Playful rounded", "Premium editorial", "Natural / organic hand-drawn"], default: "Modern minimal sans-serif" },
      { id: "iconIdea", label: "Icon or symbol idea (optional)", type: "text", placeholder: "A rising sun hidden in the letter O" },
      { id: "background", label: "Background", type: "select", options: ["Plain white", "Plain black", "Solid brand color"], default: "Plain white" },
      { id: "aspectRatio", label: "Aspect ratio", type: "select", options: ["1:1", "3:2", "16:9"], default: "1:1" },
    ],
    template: `Act as a senior brand identity designer presenting a finished logo concept. Generate one clean, professional logo design.

LOGO
A {{logoType|lower}} logo for the brand "{{brandName}}", {{category|article}} brand. The text must read exactly "{{brandName}}" — correct spelling, every letter present, no extra words, no tagline.
{{#if logoType=Monogram / lettermark}}
Build the mark from the initial letter(s) of "{{brandName}}", with the full name "{{brandName}}" set small beneath it.
{{/if}}
{{#if logoType=Emblem / badge}}
Contain the name "{{brandName}}" inside a simple geometric badge shape (circle, shield or rounded rectangle) with one supporting symbol.
{{/if}}
{{#if logoType=Wordmark + icon lockup}}
A simple, single-weight icon sits to the left of or above the wordmark "{{brandName}}", both aligned on a shared baseline.
{{/if}}
{{#if iconIdea}}
Symbol concept: {{iconIdea}}.
{{/if}}

STYLE
{{styleDirection}}. Custom-feeling letterforms, balanced letter-spacing, optically even stroke weights, flat vector style, sharp edges, no 3D, no bevels, no drop shadows.
Brand character: {{personality|join}}{{#unless personality}}confident, clean, modern{{/unless}}. Visual mood: {{style|join}}{{#unless style}}premium, minimal, distinctive{{/unless}}.
{{#if positioning}}
The logo should feel like: {{positioning}}
{{/if}}

COLOR
Background: {{background|lower}}, completely flat and empty, logo centered with generous margin on all sides.
{{#if brandColors}}
Use only these colors in the logo: {{brandColors|join}}. Maximum two colors in the logo itself.
{{/if}}
{{#unless brandColors}}
One dark ink color for the logo plus a single accent color; maximum two colors.
{{/unless}}

PRESENTATION
Logo presented alone, centered, large, as a vector-style graphic on a flat background — like a page from a brand guidelines PDF. Nothing else in the frame.

{{#if avoid}}
AVOID
{{avoid|list}}
- Misspellings, missing or duplicated letters, extra text
- Mockups, packaging, business cards, stationery, photographic scenes
- Gradients, glows, 3D effects, clip-art symbols, generic category icons
{{/if}}
{{#unless avoid}}
AVOID
- Misspellings, missing or duplicated letters, extra text
- Mockups, packaging, business cards, stationery, photographic scenes
- Gradients, glows, 3D effects, clip-art symbols, generic category icons
{{/unless}}

NEGATIVE PROMPT
misspelled text, extra letters, blurry, photograph, 3D render, gradient, drop shadow, bevel, watermark, mockup, busy background, multiple logos, collage

ASPECT RATIO
{{aspectRatio}}`,
    tips: [
      "Use Ideogram first — it spells the brand name correctly most often. Generate 4 and pick the one that reads best at thumbnail size.",
      "Upload your existing logo or a sketch first if you want ChatGPT to refine rather than invent.",
      "Then run Turn AI Logo Into Usable Vector Artwork to make the winner print-ready.",
    ],
  },

  // ── Brand story & taglines ────────────────────────────────────────────────
  {
    id: "branding-story-tagline",
    category: "branding",
    title: "Create Brand Story & Taglines",
    description: "A brand manifesto, your story in 50, 100 and 300 words, and 15 taglines across five styles with usage notes — all in your brand voice.",
    outputType: "text",
    recommendedTools: ["claude", "chatgpt", "gemini"],
    estimatedTime: "2 min to fill · 1 min in Claude",
    fields: [
      { id: "brandName", label: "Brand name", type: "text", placeholder: "XYZ", required: true, brandKey: "brandName" },
      { id: "category", label: "Product category", type: "text", placeholder: "Energy drink", required: true, brandKey: "category" },
      { id: "productDescription", label: "Product description", type: "textarea", placeholder: "12 oz sleek can, zero sugar, 200 mg natural caffeine", brandKey: "productDescription", span: "full" },
      { id: "positioning", label: "Brand positioning", type: "textarea", placeholder: "Natural premium performance energy", required: true, brandKey: "positioning", span: "full" },
      { id: "targetConsumer", label: "Target consumer", type: "textarea", placeholder: "Men and women 18–35 interested in fitness", required: true, brandKey: "targetConsumer", span: "full" },
      { id: "founderStory", label: "Founder story", type: "textarea", placeholder: "Started by two trainers who were tired of...", brandKey: "founderStory", span: "full", help: "The more real detail here, the better the story." },
      { id: "brandVoice", label: "Brand voice", type: "textarea", placeholder: "Direct and encouraging. Short sentences.", brandKey: "brandVoice", span: "full" },
      { id: "personality", label: "Brand personality", type: "tags", placeholder: "Confident, Clean, Energetic", brandKey: "personality" },
      { id: "claims", label: "Key claims", type: "tags", placeholder: "Zero sugar, Natural caffeine", brandKey: "claims" },
      { id: "competitors", label: "Competitors", type: "tags", placeholder: "Celsius, Alani Nu, Ghost", brandKey: "competitors" },
      { id: "avoid", label: "What should AI avoid?", type: "textarea", placeholder: "Hype words; 'fuel your grind'", brandKey: "avoid" },
      { id: "storyAngle", label: "Story angle", type: "select", options: ["Let AI recommend", "Founder journey", "Ingredient or origin story", "Problem we refused to accept", "Mission / purpose", "Craft and process"], default: "Let AI recommend" },
      { id: "taglineTone", label: "Tagline tone", type: "select", options: ["Confident and direct", "Warm and human", "Witty and playful", "Bold and provocative", "Calm and premium"], default: "Confident and direct" },
    ],
    template: `Act as a senior brand storyteller and copywriter who has written founding stories and taglines for challenger CPG brands — you write with specificity, never with marketing filler.

Write the brand story and tagline system for {{brandName}}, {{category|article}}.{{#if productDescription}} Product: {{productDescription}}.{{/if}} Story angle: {{storyAngle}}. Tagline tone: {{taglineTone|lower}}.

BRAND POSITIONING
{{positioning}}

TARGET CONSUMER
{{targetConsumer}}

{{#if founderStory}}
FOUNDER STORY (raw material — use the real details)
{{founderStory}}
{{/if}}
{{#unless founderStory}}
FOUNDER STORY
None provided — build the story on the product truth and the consumer's problem, and mark any place where a real founder detail should be inserted with [INSERT DETAIL].
{{/unless}}

VOICE
{{#if brandVoice}}
{{brandVoice}}
{{/if}}
{{#if personality}}
Personality: {{personality|join}}.
{{/if}}
{{#unless brandVoice}}
{{#unless personality}}
Confident, clear and human. Short sentences. No hype.
{{/unless}}
{{/unless}}

{{#if claims}}
PRODUCT TRUTHS TO GROUND THE STORY
{{claims|list}}
{{/if}}

{{#if competitors}}
COMPETITIVE SET
{{competitors|join}}. Note the story and tagline conventions they share, then avoid them.
{{/if}}

{{#if avoid}}
AVOID
{{avoid|list}}
- Cliché openers ("It all started when...", "We believe...")
- Health or performance promises that would need clinical proof
{{/if}}
{{#unless avoid}}
AVOID
- Cliché openers ("It all started when...", "We believe...")
- Health or performance promises that would need clinical proof
{{/unless}}

PROVIDE
1. Brand manifesto: 120–160 words, written to be read aloud, present tense, one idea per line
2. Brand story — 50 words: the version for a pack back panel or Instagram bio
3. Brand story — 100 words: the version for an Amazon "About the brand" module or sell-sheet sidebar
4. Brand story — 300 words: the version for the website About page, with a suggested headline and two subheads
5. 15 taglines, 3 in each of these five styles, in a table: tagline | style | the idea behind it | best use (pack, ad, website, social) | watch-out (could it be misread, is it close to a known tagline?):
   (a) Benefit-led, (b) Attitude / point of view, (c) Rallying cry for the consumer, (d) Category-flip (says what we are not), (e) Short and ownable (1–3 words)
6. Top 3 taglines ranked, with a paragraph each on why it could last 5 years
7. Trademark note: flag any tagline that resembles a known brand's line and state that tagline trademarks should be screened by an attorney before use on pack

Every piece must be usable as-is. Keep all sentences short enough to read on a phone.`,
    tips: [
      "Ask Claude: “Rewrite the 100-word story in the first person as the founder, keeping every fact.”",
      "Paste the top 3 taglines into Create Logo Concept Image as a lockup to see how they sit under the logo.",
    ],
  },

  // ── Brand voice guide ─────────────────────────────────────────────────────
  {
    id: "branding-voice-guide",
    category: "branding",
    title: "Create Brand Voice Guide",
    description: "A practical voice guide — tone pillars, vocabulary lists, grammar rules, before/after rewrites and channel adaptations — so every writer, freelancer and AI sounds like your brand.",
    outputType: "text",
    recommendedTools: ["claude", "chatgpt", "gemini"],
    estimatedTime: "2 min to fill · 1 min in Claude",
    fields: [
      { id: "brandName", label: "Brand name", type: "text", placeholder: "XYZ", required: true, brandKey: "brandName" },
      { id: "category", label: "Product category", type: "text", placeholder: "Energy drink", required: true, brandKey: "category" },
      { id: "productDescription", label: "Product description", type: "textarea", placeholder: "12 oz sleek can, zero sugar, 200 mg natural caffeine", brandKey: "productDescription", span: "full" },
      { id: "positioning", label: "Brand positioning", type: "textarea", placeholder: "Natural premium performance energy", required: true, brandKey: "positioning", span: "full" },
      { id: "targetConsumer", label: "Target consumer", type: "textarea", placeholder: "Men and women 18–35 interested in fitness", required: true, brandKey: "targetConsumer", span: "full" },
      { id: "personality", label: "Brand personality", type: "tags", placeholder: "Confident, Clean, Energetic", brandKey: "personality" },
      { id: "brandVoice", label: "Brand voice (as you describe it today)", type: "textarea", placeholder: "Direct and encouraging. Short sentences. No hype words.", brandKey: "brandVoice", span: "full" },
      { id: "founderStory", label: "Founder story (optional)", type: "textarea", placeholder: "Started by two trainers who...", brandKey: "founderStory", span: "full" },
      { id: "claims", label: "Key claims", type: "tags", placeholder: "Zero sugar, Natural caffeine", brandKey: "claims" },
      { id: "competitors", label: "Competitors", type: "tags", placeholder: "Celsius, Alani Nu, Ghost", brandKey: "competitors" },
      { id: "channels", label: "Sales channels", type: "tags", placeholder: "DTC, Amazon, Natural grocery", brandKey: "channels" },
      { id: "avoid", label: "What should AI avoid?", type: "textarea", placeholder: "Hype words; exclamation points; bro talk", brandKey: "avoid" },
      { id: "sampleCopy", label: "Existing copy to rewrite (optional)", type: "textarea", placeholder: "Paste a product description, email or caption you have written so the guide can show before/after.", span: "full" },
      { id: "formality", label: "Formality", type: "select", options: ["Casual — like a friend", "Conversational — warm but polished", "Considered — premium and precise"], default: "Conversational — warm but polished" },
    ],
    template: `Act as a senior brand voice strategist who has written voice guidelines for challenger food and beverage brands and trained their teams, agencies and AI tools to use them.

Create a brand voice guide for {{brandName}}, {{category|article}}.{{#if productDescription}} Product: {{productDescription}}.{{/if}} Formality: {{formality}}.

BRAND POSITIONING
{{positioning}}

TARGET CONSUMER
{{targetConsumer}}
Write for how this person actually talks and reads, not for how brands talk at them.

{{#if personality}}
PERSONALITY
{{personality|join}}.
{{/if}}

{{#if brandVoice}}
VOICE AS DESCRIBED TODAY
{{brandVoice}}
Sharpen this into rules; challenge anything vague.
{{/if}}

{{#if founderStory}}
FOUNDER STORY
{{founderStory}}
{{/if}}

{{#if claims}}
CLAIMS WE TALK ABOUT
{{claims|list}}
{{/if}}

{{#if competitors}}
COMPETITIVE SET
{{competitors|join}}. Identify the verbal tics they share so our voice can sound unmistakably different.
{{/if}}

{{#if channels}}
CHANNELS
{{channels|join}}.
{{/if}}

{{#if sampleCopy}}
EXISTING COPY TO REWRITE
{{sampleCopy}}
{{/if}}

{{#if avoid}}
AVOID
{{avoid|list}}
- Hype words and empty superlatives
- Health, treatment or cure language
{{/if}}
{{#unless avoid}}
AVOID
- Hype words and empty superlatives
- Health, treatment or cure language
{{/unless}}

PROVIDE
1. Voice in one sentence, then "we sound like ___, never like ___"
2. Four tone pillars: for each, a name, a definition, a "this, not that" pair of example sentences, and the trap to avoid
3. Vocabulary: a table with columns: words and phrases we use | words we never use | the on-brand replacement
4. Grammar and style rules: sentence length, contractions, punctuation (exclamation points, em dashes, emoji), capitalization, numerals, how we write the brand name and product names, how we refer to the consumer
5. Before / after rewrites: 5 short pieces rewritten into the voice — a product description, an Instagram caption, a customer-service reply to a complaint, a retail-buyer email opener and a website headline{{#if sampleCopy}} — and a sixth: the existing copy provided above{{/if}}
6. Channel adaptations: a table with rows for pack, website, Amazon listing, Instagram / TikTok, email, retail buyer outreach and customer service — columns: what stays constant | what flexes | example line
7. Tone dial: how the voice shifts across launch hype, everyday social, a supply issue or recall and a one-to-one customer reply
8. Claims language guardrails: how to talk about the product's benefits without making disease, treatment or unprovable performance claims — flag phrases that would need regulatory review
9. A 10-line "voice cheat sheet" to paste at the top of any AI prompt or freelancer brief

Every rule must come with an example. Keep the whole guide usable in under 5 minutes of reading.`,
    tips: [
      "Paste section 9 into the instructions of a Claude Project or a ChatGPT custom GPT — every future draft will stay on-brand.",
      "Follow-up: “Now grade this caption against the guide, 1–10 per pillar, and rewrite it.”",
    ],
  },

  // ── Vectorize an AI logo ──────────────────────────────────────────────────
  {
    id: "branding-vectorize-logo",
    category: "branding",
    title: "Turn AI Logo Into Usable Vector Artwork",
    description: "A step-by-step walkthrough for converting your AI-generated logo into clean vector files, with the exact formats, color modes and versions printers and co-packers will ask for.",
    outputType: "text",
    recommendedTools: ["vectorizer", "illustrator", "recraft"],
    estimatedTime: "1 min to fill · 2 min in ChatGPT",
    fields: [
      { id: "brandName", label: "Brand name", type: "text", placeholder: "XYZ", required: true, brandKey: "brandName" },
      { id: "category", label: "Product category", type: "text", placeholder: "Energy drink", required: true, brandKey: "category" },
      { id: "logoDescription", label: "Describe the logo image you have", type: "textarea", placeholder: "A black wordmark with a small orange sun icon, PNG on a white background, generated in Ideogram, about 1024 px wide", required: true, span: "full", help: "Elements, colors, background, resolution, any text." },
      { id: "brandColors", label: "Existing brand colors (optional)", type: "tags", placeholder: "#111111, #FF5A1F", brandKey: "brandColors" },
      { id: "fonts", label: "Fonts (if you know them)", type: "tags", placeholder: "Inter", brandKey: "fonts" },
      { id: "style", label: "Desired style", type: "tags", placeholder: "Premium, Modern, Minimal", brandKey: "visualStyle" },
      { id: "avoid", label: "What should AI avoid?", type: "textarea", placeholder: "Changing the letterforms; adding effects", brandKey: "avoid" },
      { id: "sourceTool", label: "Tool that generated the logo", type: "select", options: ["Ideogram", "ChatGPT image generation", "Midjourney", "Recraft", "Canva", "Other / not sure"], default: "Ideogram" },
      { id: "toolsAvailable", label: "Tools you have access to", type: "select", options: ["Not sure — recommend the cheapest path", "Vectorizer.AI + Canva (no Adobe)", "Adobe Illustrator", "Recraft", "Figma", "Affinity Designer"], default: "Not sure — recommend the cheapest path" },
      { id: "experienceLevel", label: "Your design experience", type: "select", options: ["None — explain every click", "Some — I have used Canva", "Comfortable in design software"], default: "None — explain every click" },
      { id: "format", label: "First print application", type: "select", options: ["Can or bottle label", "Pouch or bag", "Folding carton or box", "Shipping box", "Sell sheet / print collateral", "Digital only for now"], default: "Can or bottle label" },
      { id: "printerRequests", label: "What the printer or co-packer asked for (optional)", type: "textarea", placeholder: "Vector EPS, CMYK, fonts outlined, Pantone references", span: "full" },
    ],
    template: `Act as a senior production artist and prepress specialist who prepares CPG brand artwork for label printers and co-packers, and who teaches founders with zero design background how to do it themselves.

Walk me step by step through turning my AI-generated logo for {{brandName}}, {{category|article}}, into clean, print-ready vector artwork. My experience level: {{experienceLevel}}. Tools I have: {{toolsAvailable}}. First print application: {{format|lower}}.

THE LOGO I HAVE
{{logoDescription}}
Generated in: {{sourceTool}}.
{{#if brandColors}}
Intended brand colors: {{brandColors|join}}.
{{/if}}
{{#if fonts}}
Fonts it should match or be rebuilt with: {{fonts|join}}.
{{/if}}
{{#if style}}
The finished mark must still feel: {{style|join}}.
{{/if}}

{{#if printerRequests}}
WHAT THE PRINTER ASKED FOR
{{printerRequests}}
Make sure every item here is covered by the end of the walkthrough.
{{/if}}

{{#if avoid}}
AVOID
{{avoid|list}}
- Changing the design while vectorizing — this is a conversion, not a redesign
- Leaving live text in final files
{{/if}}
{{#unless avoid}}
AVOID
- Changing the design while vectorizing — this is a conversion, not a redesign
- Leaving live text in final files
{{/unless}}

PROVIDE
1. Prep the source image: how to get the cleanest possible input (regenerate at the highest resolution, plain white or transparent background, no shadows or mockups), and how to crop and upscale it if needed
2. Vectorize — the recommended path for my tools, with numbered click-by-click steps. Cover the relevant options: Vectorizer.AI (upload, settings, download SVG), Recraft (vectorize or regenerate as native vector), Adobe Illustrator (Image Trace presets, Expand, Ungroup) and a note on when to hire a designer for 1–2 hours instead
3. Clean-up: simplifying anchor points, merging shapes, fixing wobbly curves, checking symmetry and alignment, removing stray specks — explained for my experience level
4. Rebuild the type: why AI-generated letters should usually be re-set with a real typeface, how to identify the closest font (free and paid options), and how to outline text so the printer never needs the font file
5. Color modes explained simply: RGB vs. CMYK vs. Pantone spot colors, which one each use case needs, how to convert my hex colors{{#if brandColors}} ({{brandColors|join}}){{/if}} and why I should ask the printer to confirm the Pantone match on a physical proof
6. Logo versions to create: full color, one-color black, one-color white (reversed), and grayscale; plus horizontal, stacked and icon-only lockups; minimum size and clear space
7. File formats table: format | what it is for | who asks for it | settings — covering SVG, AI, EPS, PDF (print-ready with bleed notes), PNG transparent (sizes for web and Amazon), JPG
8. File naming and folder structure so the printer, the co-packer and my social manager each find the right file
9. Pre-flight checklist for {{format|lower}}: the 10 things to verify before sending artwork
10. Questions to ask the printer before sending anything (dieline, bleed, minimum line weight, white underprint, finishes)
11. Common mistakes founders make at this stage and how to catch them

Explain every step in plain language. Where a step differs by tool, give each tool its own mini list.`,
    tips: [
      "Upload your logo PNG to ChatGPT alongside this prompt so the advice is specific to your actual image.",
      "Start with Vectorizer.AI — it takes 60 seconds and the SVG opens in Canva, Figma or Illustrator.",
      "Ask your printer for their artwork spec sheet first and paste it into the optional field — the walkthrough will match it.",
    ],
  },
];
